import type { Settings } from './Settings';
import type { i18n } from 'i18next';

import { PCON_LOGIN_SESSION_REQUIRED } from './Constants';
import { pConServiceProvider } from './ServicesProvider';

import { appInfo } from '@egr/xbox/app-api/AppInfo';
import { buildQueryString, stripHash, stripQueryString, extendQueryString } from '@egr/xbox/utils/Url';
import { getObjectFromQueryString } from '@egr/xbox/utils/string/url/getObjectFromQueryString';

import { Service } from '@easterngraphics/pcon-services';
import { isNullOrEmpty, isNotNullOrEmpty, joinPath } from '@easterngraphics/wcf/modules/utils/string';

const OAUTH_CALLBACK_URL: () => string = (): string => {
    const path: string = document.location.pathname.substr(0, document.location.pathname.lastIndexOf('/'));

    if (appInfo != null && !document.location.protocol.startsWith('http')) {
        return stripHash(stripQueryString(appInfo.initialization_url)) + 'oauth_callback.html';
    }

    return document.location.protocol + '//' + document.location.host + path + '/oauth_callback.html';
};

const pconLoginUrlFraments: Array<string> = ['access_token', 'expires_in', 'token_type', 'scope', 'state'];

export interface SettingProviderOptions {
    clientId: string;
    i18n?: i18n;
    callbackMode: 'redirect' | 'postMessage';
    /**
     * will be forced if env PCON_LOGIN_SESSION_REQUIRED is set `true`
     */
    requiresLoginSession?: boolean;
}

export class SettingProvider implements Settings {
    public constructor(protected options: SettingProviderOptions) {
    }

    public get profileUrl(): string {
        return pConServiceProvider.getUrl(Service.pCon_login);
    }

    public get authApiUrl(): string {
        const hash: string = document.location.hash.slice(1);
        if (this.options.callbackMode !== 'redirect' || isNotNullOrEmpty(hash)) {
            // The hash part should be used for debug purpose only!
            // However, it won't work with pCon.login -> we have to use a little hack.

            const query = getObjectFromQueryString(document.location.search.slice(1));
            if (isNotNullOrEmpty(hash)) {
                // Delete pCon.login related parameters
                const hashQuery = getObjectFromQueryString(hash);
                for (const key of pconLoginUrlFraments) {
                    delete hashQuery[key];
                }

                query.fragment = buildQueryString(hashQuery);
            }

            query.callback_mode = this.options.callbackMode;

            let baseUrl: string = stripHash(stripQueryString(document.location.href));
            if (!baseUrl.endsWith('/')) {
                baseUrl = baseUrl.substr(0, baseUrl.lastIndexOf('/') + 1);
            }

            return this.buildAuthApiUrl(extendQueryString(OAUTH_CALLBACK_URL(), query));
        }

        return this.buildAuthApiUrl();
    }

    public get backgroundAuthApiUrl(): string {
        return this.buildAuthApiUrl(
            extendQueryString(
                OAUTH_CALLBACK_URL(),
                {
                    callback_mode: 'postMessage'
                }
            ),
            {
                no_login: 1
            }
        );
    }

    public get userApiUrl(): string {
        return joinPath(pConServiceProvider.getUrl(Service.pCon_login), 'api/v1/account/user?embed=account_organization');
    }

    public get logoutApiUrl(): string {
        return joinPath(pConServiceProvider.getUrl(Service.pCon_login), 'api/v1/logout');
    }

    public get organizationApiUrl(): string {
        return joinPath(pConServiceProvider.getUrl(Service.pCon_login), 'api/v1/account/organization');
    }

    public get resellerApiUrl(): string {
        return joinPath(pConServiceProvider.getUrl(Service.pCon_login), 'api/v1/account/reseller/contact');
    }

    public get requiresLoginSession(): boolean {
        return this.options.requiresLoginSession ?? PCON_LOGIN_SESSION_REQUIRED;
    }

    protected buildAuthApiUrl(url: string = document.location.href, additionalArguments?: Record<string, string | number | null | undefined>): string {
        const userLanguage: string | undefined = this.getUserLanguage();
        return joinPath(pConServiceProvider.getUrl(Service.pCon_login), 'api/oauth2/authorize?' + buildQueryString(
            Object.assign(
                {
                    lang: isNotNullOrEmpty(userLanguage) ? userLanguage : 'en',
                    client_id: this.options.clientId,
                    response_type: 'token',
                    redirect_uri: stripHash(url),
                    state: '123456' // FixMe: use real state
                },
                additionalArguments
            )
        ));
    }

    protected getUserLanguage(): string | undefined {
        if (
            this.options.i18n == null ||
            isNullOrEmpty(this.options.i18n.language) ||
            isNullOrEmpty(this.options.i18n.language.split('-')[0])
        ) {
            return;
        }

        return this.options.i18n.language.split('-')[0];
    }
}